import { createContext, useState, useEffect, useCallback, useRef } from "react";

export const ThemeAudioContext = createContext();

export const ThemeAudioProvider = ({ children }) => {
  const audioRef = useRef(null); // persistent across renders
  const [enabled, setEnabled] = useState(true);
  const originalVolumeRef = useRef(1);

  // Load saved toggle state from sessionStorage
  useEffect(() => {
    const saved = sessionStorage.getItem("isThemeAudioEnabled");
    if (saved === null) {
      sessionStorage.setItem("isThemeAudioEnabled", "true");
      setEnabled(true);
    } else {
      setEnabled(saved === "true");
    }
  }, []);

  // Set audio source once
  const setAudioSource = useCallback((url) => {
    if (!url) return;

    // Only create Audio if it doesn't exist
    if (!audioRef.current) {
      audioRef.current = new Audio(url);
      audioRef.current.loop = true;

      const isEnabled =
        sessionStorage.getItem("isThemeAudioEnabled") === "true";

      if (isEnabled) {
        audioRef.current.play().catch(() => {});
      }

      // If autoplay blocked, play on first user gesture
      const resume = () => {
        if (isEnabled && audioRef.current) {
          audioRef.current.play().catch(() => {});
        }
        window.removeEventListener("click", resume);
        window.removeEventListener("touchstart", resume);
      };

      window.addEventListener("click", resume);
      window.addEventListener("touchstart", resume);
    } else if (audioRef.current.src !== url) {
      // If URL changes (rare), update without resetting time
      const currentTime = audioRef.current.currentTime;
      audioRef.current.src = url;
      audioRef.current.currentTime = currentTime;
    }
  }, []);

  // Toggle ON/OFF from any component
  const toggleAudio = () => {
    setEnabled((prev) => {
      const newState = !prev;
      sessionStorage.setItem(
        "isThemeAudioEnabled",
        newState ? "true" : "false"
      );

      if (audioRef.current) {
        if (newState) audioRef.current.play().catch(() => {});
        else audioRef.current.pause();
      }

      return newState;
    });
  };

  // Temporarily pause the theme audio
  const pauseThemeAudio = () => {
    const isEnabled = sessionStorage.getItem("isThemeAudioEnabled") === "true";
    if (audioRef.current && isEnabled) {
      audioRef.current.pause();
    }
  };

  const fadeVolume = (from, to, duration = 500) => {
    return new Promise((resolve) => {
      const audio = audioRef.current;
      if (!audio) return resolve();

      const start = performance.now();

      const animate = (now) => {
        const progress = Math.min((now - start) / duration, 1);
        audio.volume = from + (to - from) * progress;

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          resolve();
        }
      };

      requestAnimationFrame(animate);
    });
  };

  // Resume the theme audio if enabled
  const resumeThemeAudio = () => {
    const isEnabled = sessionStorage.getItem("isThemeAudioEnabled") === "true";
    if (audioRef.current && isEnabled) {
      audioRef.current.play().catch(() => {});
    }
  };

  const reduceThemeAudioVolume = () => {
    const audio = audioRef.current;
    if (!audio) return;

    let target = 0.005;
    let duration = 400;
    let frameRate = 30;
    let step = (audio.volume - target) / (duration / frameRate);

    return new Promise((resolve) => {
      let fade = setInterval(() => {
        let next = audio.volume - step;
        audio.volume = Math.max(next, target); // prevent undershoot

        if (audio.volume <= target + 0.001) {
          audio.volume = target;
          clearInterval(fade);
          resolve();
        }
      }, frameRate);
    });
  };

  // Safe fade up
  const restoreThemeAudioVolume = () => {
    const audio = audioRef.current;
    if (!audio) return;

    let target = originalVolumeRef.current || 1;
    let duration = 400;
    let frameRate = 30;
    let step = (target - audio.volume) / (duration / frameRate);

    return new Promise((resolve) => {
      let fade = setInterval(() => {
        let next = audio.volume + step;
        audio.volume = Math.min(next, target); // prevent overshoot

        if (audio.volume >= target - 0.001) {
          audio.volume = target;
          clearInterval(fade);
          resolve();
        }
      }, frameRate);
    });
  };

  return (
    <ThemeAudioContext.Provider
      value={{
        enabled,
        toggleAudio,
        setAudioSource,
        pauseThemeAudio,
        resumeThemeAudio,
        reduceThemeAudioVolume,
        restoreThemeAudioVolume,
        audioRef,
      }}
    >
      {children}
    </ThemeAudioContext.Provider>
  );
};
